import { Box } from '@mui/material';
import { useCaseFieldData } from '../../hooks/useCaseField';
import type { FieldMode } from '../../hooks/useCaseField';
import { CaseTextField, CaseSwitchField, CaseChipListField } from '../fields';
import type { MedNecCaseView, SurgeryInfo } from '../../../../services/MedNecService/types/medNecTypes';

interface SurgerySectionProps {
  mode: FieldMode;
}

// Helper: read a surgery text field
const s = (field: keyof SurgeryInfo) => (d: MedNecCaseView) => (d.surgery?.[field] as string) ?? '';

// Helper: produce an update that merges a single surgery field
const sUp = (field: keyof SurgeryInfo) => (v: string, d: MedNecCaseView): Partial<MedNecCaseView> => ({
  surgery: { ...d.surgery!, [field]: v },
});

const SurgerySection: React.FC<SurgerySectionProps> = ({ mode }) => {
  const data = useCaseFieldData(mode);
  if (!data.surgery) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 1.5 }}>
        <CaseTextField mode={mode} label="Surgery Date" type="date" shrinkLabel
          getValue={d => { const v = s('surgeryDate')(d); return v.includes('T') ? v.split('T')[0] : v; }}
          getUpdate={sUp('surgeryDate')} />
        <CaseTextField mode={mode} label="Surgery Time" type="time" shrinkLabel
          getValue={s('surgeryTime')} getUpdate={sUp('surgeryTime')} />
        <CaseTextField mode={mode} label="Duration" getValue={s('duration')} getUpdate={sUp('duration')} />
        <CaseTextField mode={mode} label="Position" getValue={s('position')} getUpdate={sUp('position')} />
        <CaseTextField mode={mode} label="Anesthesia Type" getValue={s('anesthesiaType')} getUpdate={sUp('anesthesiaType')} />
        <CaseTextField mode={mode} label="Implant Vendor" getValue={s('implantVendor')} getUpdate={sUp('implantVendor')} />
        <CaseTextField mode={mode} label="Implant Rep" getValue={s('implantRepInfo')} getUpdate={sUp('implantRepInfo')} />
        <CaseSwitchField mode={mode} label="Implants Required"
          getValue={d => d.surgery?.implantsRequired ?? false}
          getUpdate={(checked, d) => ({ surgery: { ...d.surgery!, implantsRequired: checked } })} />
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <CaseChipListField mode={mode} label="Equipment"
          getItems={d => d.surgery?.equipment ?? []}
          getUpdate={(items, d) => ({ surgery: { ...d.surgery!, equipment: items } })} />
      </Box>
      <CaseTextField mode={mode} label="Special Instructions" fullWidth multiline minRows={2} maxRows={6}
        getValue={s('specialInstructions')} getUpdate={sUp('specialInstructions')} />
    </Box>
  );
};

export default SurgerySection;
